<template>
  <ion-page id="main-content">
    <ion-header v-if="!hideHeader">
      <ion-toolbar
        mode="ios"
        :color="
          inIframe && config.app.webxEmbed.useSecondaryHeaderColour
            ? 'secondary'
            : 'white'
        "
      >
        <ion-buttons slot="start">
          <ion-button
            v-if="backFunction"
            v-tooltip="$t('tooltip.baseLayout.backButton')"
            color="primary"
            :disabled="disableButtons || showLoading"
            @click="backFunction"
          >
            <ion-icon :icon="chevronBackOutline" style="margin-left: -10px" />
            {{ $t('base.back') }}
          </ion-button>

          <ion-back-button
            v-else-if="showBackButton"
            v-tooltip="$t('tooltip.baseLayout.backButton')"
            :default-href="defaultBackLinkUrl"
            :text="$t('base.back')"
            :disabled="disableButtons || showLoading"
            color="primary"
          />

          <ion-button
            v-else
            v-tooltip="config.app.companyDetails.name"
            :href="config.app.companyDetails.url"
            target="_blank"
            expand="block"
            fill="clear"
            :disabled="disableButtons || showLoading"
          >
            <ion-img
              class="company-favicon ion-margin-end"
              src="../assets/favicon.png"
              :alt="$t('base.operatorLogo')"
            />
          </ion-button>
        </ion-buttons>
        <ion-title color="primary"> {{ title }}</ion-title>
        <ion-buttons slot="end">
          <ion-menu-button
            v-if="showMenu && store.isSignedIntoIntegra"
            v-tooltip.left="$t('tooltip.baseLayout.menuButton')"
            :disabled="disableButtons || showLoading"
            color="primary"
          />
          <signed-out-buttons
            v-else-if="showMenu"
            :disabled="disableButtons || showLoading"
          />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content :scroll-y="!disableScroll">
      <loading-spinner
        v-if="showLoading"
        :use-loading-timeout="useLoadingTimeout"
        @loading-timeout="showLoading = false"
      />
      <div v-if="isError">{{ $t('base.errorOccured') }}</div>
      <div v-else :class="{ 'fixed-grid': fixedGrid }">
        <slot />
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonImg,
  IonMenuButton,
  IonButton,
  IonIcon,
} from '@ionic/vue';
import { chevronBackOutline } from 'ionicons/icons';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import SignedOutButtons from '@/components/SignedOutButtons.vue';
import { ref, watch } from 'vue';
import config from '@/config';
import { useResponsive } from '@/composables/useResponsive';
import { useStore } from '@/store';

const props = withDefaults(
  defineProps<{
    title?: string;
    defaultBackLinkUrl?: string;
    showBackButton?: boolean;
    showMenu?: boolean;
    isError?: boolean;
    backFunction?: (...args: any[]) => any;
    disableButtons?: boolean;
    isLoading?: boolean;
    fixedGrid?: boolean;
    useLoadingTimeout?: boolean;
    hideHeader?: boolean;
    disableScroll?: boolean;
  }>(),
  {
    title: '',
    defaultBackLinkUrl: '/',
    showBackButton: true,
    showMenu: true,
    isError: false,
    disableButtons: false,
    isLoading: false,
    fixedGrid: true,
    useLoadingTimeout: false,
    hideHeader: false,
    disableScroll: false,
  },
);

const { inIframe } = useResponsive();
const store = useStore();

const showLoading = ref(props.isLoading);

watch(
  () => props.isLoading,
  (loading) => {
    showLoading.value = loading;
  },
);
</script>

<style scoped>
.fixed-grid {
  max-width: 1280px;
  margin: 0 auto;
}
</style>
